import { cn } from '@/lib/cn';
import type { Card, LineResult } from '@/lib/qcp/protocol';
import { conSegno } from '@/lib/qcp/resoconto';
import { PlayingCard } from './PlayingCard';
import type { TagliaCp } from './cartaCp';

interface OfcRowProps {
  label: 'TOP' | 'MID' | 'BOT';
  cards: Card[];
  capacity: 3 | 5;
  /** Called with the slot index when a slot (filled or empty) is tapped. */
  onSlotTap?: (index: number) => void;
  /** Highlight this row as a legal placement target: the «invito» (a gradient that runs along the row and breathes). */
  highlight?: boolean;
  /** Il dito che trascina una carta e SOPRA questa fila (che e un bersaglio legale): si accende piu dell'invito. */
  sopra?: boolean;
  /** Keys (`${r}${s}`) of cards placed THIS hand — drawn with a dim ring. */
  pendingKeys?: Set<string>;
  /** Key of the currently picked-up pending card — drawn lifted, with a bright ring. */
  pickedKey?: string;
  /** La taglia delle carte (v. `cartaCp.ts`). Senza, `mia`. */
  taglia?: TagliaCp;
  quattroColori?: boolean;
  /**
   * Il contorno della fila: `filo` = un filo d'oro tenue (la fila ha ancora spazio), `nessuno` = niente (default). Il contorno dell'invito lo da `highlight`.
   */
  contorno?: 'filo' | 'nessuno';
  /** Il RESOCONTO: l'esito della fila (+1/−1/0) e le royalty pagate (mostrate solo se > 0), nella colonna a destra. `esito` nullo = il server non lo manda (foul). */
  esito?: LineResult | null;
  royalty?: number;
  /** Il resoconto e acceso: serve a lasciare la colonna a destra anche quando non c'e niente da scrivere. */
  conResoconto?: boolean;
  /** Le file di chi ha fatto foul: sbiadite. */
  sbiadita?: boolean;
  /** «royalty», nella lingua dell'app (testo dato da chi chiama: qui non c'e nessun testo scritto). */
  etichettaRoyalty?: string;
}

const COLORE_ETICHETTA: Record<OfcRowProps['label'], string> = {
  TOP: 'text-[#d9b86b]',
  MID: 'text-brand',
  BOT: 'text-emerald-400',
};
const COLORE_ESITO = (e: LineResult) => (e > 0 ? 'text-emerald-300' : e < 0 ? 'text-rose-300' : 'text-fg-muted');

/**
 * One row of an OFC board — front (TOP/3 slots), middle (MID/5 slots), back (BOT/5 slots).
 *
 * Renders exactly `capacity` slots: filled cards + empty placeholders (tratteggiati d'oro) for unfilled slots. The row label sits to the left.
 * `highlight` = the row where the picked card can go. In the hand RESOCONTO the right column carries the row's result and its royalty.
 */
export function OfcRow({ label, cards, capacity, onSlotTap, highlight, sopra = false, pendingKeys, pickedKey, taglia = 'mia', quattroColori = false, contorno = 'nessuno', esito, royalty, conResoconto = false, sbiadita = false, etichettaRoyalty }: OfcRowProps) {
  const slots = Array.from({ length: capacity }, (_, i) => cards[i] ?? null);
  const piccola = taglia === 'avv' || taglia === 'mini';
  const stretta = piccola || taglia === 'mia';

  return (
    <div
      data-fila={label}
      data-fila-invito={highlight ? 'true' : undefined}
      data-fila-sopra={sopra ? 'true' : undefined}
      className={cn('relative flex items-center rounded-xl transition-shadow', sopra && 'tav-bersaglio', taglia === 'mini' || taglia === 'mia' ? 'gap-2 p-0.5' : stretta ? 'gap-2 p-1' : 'gap-2 p-1.5', contorno === 'filo' && !sopra && 'tav-filo-oro', sbiadita && 'opacity-90')}
    >
      {highlight && <span aria-hidden="true" className="tav-invito pointer-events-none absolute inset-0 rounded-xl" />}
      {/* Row label */}
      <span className={cn('relative w-7 shrink-0 text-center text-[10px] font-bold uppercase tracking-widest', COLORE_ETICHETTA[label])}>{label}</span>

      {/* Card slots */}
      <div className={cn('relative flex', piccola ? 'gap-[5px]' : taglia === 'mia' ? 'gap-[5px]' : 'gap-1.5')}>
        {slots.map((card, idx) => {
          const key = card ? `${card.r}${card.s}` : null;
          const isPending = key ? (pendingKeys?.has(key) ?? false) : false;
          const isPicked = key !== null && key === pickedKey;
          return (
            <PlayingCard
              key={idx}
              card={card ?? undefined}
              taglia={taglia}
              quattroColori={quattroColori}
              selected={isPicked}
              pending={isPending && !isPicked}
              sbiadita={sbiadita}
              onClick={onSlotTap ? () => onSlotTap(idx) : undefined}
            />
          );
        })}
      </div>

      {conResoconto && (
        <div data-esito-fila={esito ?? undefined} className="relative ml-auto flex min-w-[52px] flex-col items-end justify-center pr-1 text-right tabular-nums">
          {esito !== null && esito !== undefined && <span className={cn('text-[19px] font-extrabold leading-none', COLORE_ESITO(esito))}>{conSegno(esito)}</span>}
          {royalty !== undefined && royalty > 0 && (
            <span data-royalty-fila={royalty} className="mt-0.5 text-[11px] font-semibold leading-tight text-[#ecd39a]">
              {etichettaRoyalty} {conSegno(royalty)}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
